import { useEffect, useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SEOHead from "@/components/SEOHead";
import ProductCard from "@/components/ProductCard";
import KitBuilder from "@/components/KitBuilder";
import { useSeasonSale } from "@/hooks/useSeasonSale";
import { trackPixel } from "@/lib/pixel";
import { Loader2, Tag, ArrowRight } from "lucide-react";

// Assign a sale product to the first matching named section.
const lc = (v) => (v || "").toString().toLowerCase();
function bucketOf(p) {
  const cats = [lc(p.category), ...((p.categories || []).map(lc))];
  const sub = lc(p.subcategory);
  const name = lc(p.name);
  const isRugby = cats.includes("rugby");
  const isHockey = cats.includes("hockey");
  if (isRugby && (sub.includes("shirt") || sub.includes("jersey") || name.includes("jersey"))) return "RUGBY JERSEYS";
  if (isRugby && (sub.includes("short") || name.includes("short"))) return "RUGBY SHORTS";
  if (["tights", "vest", "vests", "base layer", "skort", "dress", "performance"].some((k) => sub.includes(k)) || name.includes("performance")) return "PERFORMANCE WEAR";
  if (isHockey) return "HOCKEY";
  return "OTHER SELECTED SALE ITEMS";
}
const SECTION_ORDER = ["RUGBY JERSEYS", "RUGBY SHORTS", "PERFORMANCE WEAR", "HOCKEY", "OTHER SELECTED SALE ITEMS"];

export default function Sale() {
  const { isActive, headline, endsAt } = useSeasonSale();
  const [kit, setKit] = useState(null);
  const [saleProducts, setSaleProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const viewed = useRef(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get("/bundles").then((r) => r.data).catch(() => []),
      api.get("/products", { params: { is_special: true } }).then((r) => r.data).catch(() => []),
    ]).then(([bundles, products]) => {
      if (!alive) return;
      setKit((bundles || []).find((b) => b.bundle_type === "kit") || null);
      setSaleProducts(Array.isArray(products) ? products : []);
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // ViewContent for the kit hero — once
  useEffect(() => {
    if (kit && isActive && !viewed.current) {
      viewed.current = true;
      const ids = (kit.kit_groups || []).flatMap((g) => (g.products || []).map((p) => p.product_id));
      trackPixel("ViewContent", {
        content_ids: ids, content_name: kit.name, content_type: "product",
        value: kit.bundle_price, currency: "ZAR",
      });
    }
  }, [kit, isActive]);

  const sections = useMemo(() => {
    const map = {};
    for (const p of saleProducts) {
      const b = bucketOf(p);
      (map[b] = map[b] || []).push(p);
    }
    return SECTION_ORDER.filter((s) => map[s]?.length).map((s) => ({ title: s, items: map[s] }));
  }, [saleProducts]);

  const price = Number(kit?.bundle_price || 0);
  const maxNormal = Number(kit?.compare_at_price ?? kit?.max_normal_total ?? 0);
  const kitSaving = Math.max(0, maxNormal - price);

  return (
    <div data-testid="sale-page" className="bg-[#0A0A0A] min-h-screen">
      <SEOHead
        title="End-of-Season Sale — Up to 30% Off | Untamed Gear"
        description="End-of-Season Sale at Untamed Gear — up to 30% off rugby, hockey and performance wear, plus the Complete Rugby Kit (1 jersey + 1 short) while stock lasts."
        canonicalPath="/sale"
        noindex={!isActive}
      />

      {/* Hero headline */}
      <section className="border-b-4 border-[#F7931E] bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-center">
          <p className="text-[#F7931E] text-xs sm:text-sm font-bold tracking-[0.35em] uppercase mb-4 font-heading">End-of-Season Sale</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-white leading-[0.95] font-heading">
            Up To 30% Off
          </h1>
          <p className="text-[#A3A3A3] text-sm sm:text-base mt-5 uppercase tracking-wider font-bold">
            Rugby • Hockey • Performance Wear • Selected Gear
          </p>
          {isActive && headline && (
            <p className="text-white/90 text-sm mt-4 max-w-xl mx-auto">{headline}</p>
          )}
        </div>
      </section>

      {!isActive && (
        <div data-testid="sale-inactive-notice" className="max-w-2xl mx-auto px-4 py-12 text-center">
          <Tag size={32} className="mx-auto text-[#525252] mb-4" />
          <h2 className="text-white font-bold uppercase tracking-tight text-xl font-heading mb-2">The sale isn't running right now</h2>
          <p className="text-[#A3A3A3] text-sm mb-6">Our End-of-Season Sale is currently closed. Check back soon — or browse the full range in the meantime.</p>
          <Link to="/shop" className="inline-flex items-center gap-2 bg-[#F7931E] text-black font-bold uppercase tracking-wide py-3 px-8 hover:bg-[#D97715] transition-colors btn-press">
            Shop the Range <ArrowRight size={18} />
          </Link>
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-24"><Loader2 size={32} className="animate-spin text-[#F7931E]" /></div>
      )}

      {/* Complete Rugby Kit — hero offer */}
      {isActive && !loading && kit && (
        <section data-testid="sale-kit-hero" className="py-12 sm:py-16 border-b border-[#262626]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
              <div className="lg:sticky lg:top-24">
                <span className="inline-block bg-[#F7931E] text-black text-xs font-black uppercase tracking-wider px-3 py-1 mb-4">Best Value</span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter uppercase text-white font-heading leading-none">
                  Complete Rugby Kit
                </h2>
                <p className="text-[#F7931E] font-bold uppercase tracking-[0.2em] text-sm mt-3">1 Rugby Jersey + 1 Rugby Short</p>
                <div className="mt-6 flex flex-wrap items-end gap-4">
                  {maxNormal > price && <span className="text-[#737373] line-through text-xl">Was R{maxNormal.toFixed(0)}</span>}
                  <span className="text-[#F7931E] font-black text-5xl sm:text-6xl">R{price.toFixed(0)}</span>
                </div>
                {kitSaving > 0 && (
                  <p className="text-green-400 font-bold uppercase tracking-wider text-sm mt-2">Save R{kitSaving.toFixed(0)} • While stock lasts</p>
                )}
                <p className="text-[#A3A3A3] text-sm mt-5 max-w-md">
                  Mix and match any eligible jersey with any eligible rugby short — one fixed price for the complete kit. Delivery is charged as normal; the kit does not qualify for free delivery on its own.
                </p>
              </div>
              <div className="bg-[#0A0A0A] border border-[#262626] p-4 sm:p-6">
                <KitBuilder bundle={kit} />
              </div>
            </div>
          </div>
        </section>
      )}

      {isActive && !loading && !kit && (
        <div className="max-w-2xl mx-auto px-4 py-10 text-center">
          <p className="text-[#A3A3A3] text-sm">The Complete Rugby Kit is being set up — check back shortly.</p>
        </div>
      )}

      {/* Individual sale product sections */}
      {isActive && !loading && sections.map((sec) => (
        <section key={sec.title} data-testid={`sale-section-${sec.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`} className="py-10 sm:py-14 border-b border-[#262626]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-black tracking-tighter uppercase text-white font-heading mb-6 flex items-center gap-3">
              <Tag size={20} className="text-[#F7931E]" /> {sec.title}
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {sec.items.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        </section>
      ))}

      {isActive && !loading && kit && sections.length === 0 && (
        <div className="max-w-2xl mx-auto px-4 py-12 text-center">
          <p className="text-[#A3A3A3] text-sm">More sale items are being added — build your Complete Rugby Kit above in the meantime.</p>
        </div>
      )}
    </div>
  );
}
